import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { GameCatalogEntry, GameRoomSummary, Sport } from '../../../shared/types';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';
import { useSocket, useGameRooms } from '../hooks/useSocket';
import { Navbar } from '../components/Layout/Navbar';
import { HeroCarousel } from '../components/Hub/HeroCarousel';
import { CategoryRail } from '../components/Hub/CategoryRail';
import { Carousel } from '../components/Hub/Carousel';
import { GameCard } from '../components/Hub/GameCard';
import { RoomCard } from '../components/Hub/RoomCard';
import { isSportEnabled, SINGLE_SPORT } from '../lib/sports';

type SportFilter = Sport | 'all';

/**
 * Hub / Catálogo de SportsArena.
 *
 * Estructura (inspirada en Roblox.com):
 *   1. Hero con el juego destacado del día.
 *   2. Filtro de deportes (Charts / Marketplace / Create equivalent).
 *   3. Carruseles horizontales: Featured, Trending, Popular, Recently Played.
 *   4. Listado de salas activas en tiempo real (WebSockets).
 *
 * Reglas de UX (corregidas):
 *  - La página NUNCA bloquea al usuario con alerts. Si no hay sesión,
 *    `useSupabaseAuth` ya auto-crea un guest local; y como red de
 *    seguridad, si por algún motivo `player` sigue siendo null al pulsar
 *    Jugar/Unirse, forzamos `signInAnonymously()` antes de continuar.
 *  - "Ver trailer" navega a la vista 3D del estadio en `/stadium`.
 */
export const HubPage = () => {
  const navigate = useNavigate();
  const { player, signInAnonymously, signOut } = useSupabaseAuth();
  const { socket, connected } = useSocket();
  const rooms = useGameRooms(socket);
  const [sport, setSport] = useState<SportFilter>('all');
  // Aviso al volver de otra página (p. ej. «La sala ya no existe» desde un enlace de sala vieja).
  const location = useLocation();
  // También si el Hub ya estaba abierto (p. ej. tras borrar la cuenta desde Ajustes).
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    const n = (location.state as { notice?: string } | null)?.notice;
    if (!n) return;
    setNotice(n);
    // Se quita del historial: al recargar no vuelve a salir.
    navigate('.', { replace: true, state: null });
  }, [location.state, navigate]);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 8_000);
    return () => clearTimeout(t);
  }, [notice]);

  // Catálogo stub — en Fase 2 lo traeremos desde Supabase.
  const catalog = useMemo<GameCatalogEntry[]>(() => makeStubCatalog().filter((c) => isSportEnabled(c.sport)), []);

  const featured = useMemo(
    () => catalog.filter((c) => (sport === 'all' ? true : c.sport === sport)).slice(0, 6),
    [catalog, sport]
  );

  /** Garantiza que tenemos un `player` antes de cualquier acción que lo requiera. */
  const ensurePlayer = useCallback(async () => {
    if (player) return player;
    return signInAnonymously();
  }, [player, signInAnonymously]);

  /**
   * Entra al lobby de un juego. Pública → matchmaking (sala abierta o nueva);
   * privada → siempre una sala nueva con código de acceso.
   */
  const openRoom = useCallback(
    async (entry: GameCatalogEntry, isPrivate: boolean) => {
      // Red de seguridad: si por lo que sea no hay player, lo creamos.
      const p = await ensurePlayer();
      if (!p) {
        navigate('/stadium', { state: { sport: entry.sport, title: entry.title } });
        return;
      }
      if (!socket) {
        // Sin socket (backend caído) igualmente dejamos explorar la cancha.
        navigate('/stadium', { state: { sport: entry.sport, title: entry.title } });
        return;
      }
      // `matchmake`: el servidor nos une a una sala abierta de este juego si
      // existe (si no, crea una). Antes cada pestaña creaba su propia sala.
      socket.emit(
        'room:create',
        { sport: entry.sport, name: entry.title, isPrivate, player: p, matchmake: !isPrivate },
        (res) => {
          if (res.ok) {
            navigate(`/lobby/${res.room.id}`);
          } else {
            // Si el backend rechaza, también abrimos la vista 3D pública.
            // eslint-disable-next-line no-console
            console.warn('[hub] room:create falló, abriendo vista 3D:', res.error);
            navigate('/stadium', { state: { sport: entry.sport, title: entry.title } });
          }
        }
      );
    },
    [ensurePlayer, socket, navigate]
  );

  const handlePlay = useCallback((entry: GameCatalogEntry) => openRoom(entry, false), [openRoom]);
  const handleCreatePrivate = useCallback((entry: GameCatalogEntry) => openRoom(entry, true), [openRoom]);

  const handleJoin = useCallback(
    (roomId: string) => {
      // El lobby hace el `room:join` (y gestiona errores / reconexión).
      navigate(`/lobby/${roomId}`);
    },
    [navigate]
  );

  const handleViewStadium = useCallback(
    (entry: GameCatalogEntry) => {
      navigate('/stadium', { state: { sport: entry.sport, title: entry.title, stadiumId: entry.stadiumId } });
    },
    [navigate]
  );

  // Datos reales: jugadores conectados ahora en las salas de cada deporte (lista del servidor).
  const playingBySport = useMemo(() => {
    const by: Record<Sport, number> = { futbol: 0, beisbol: 0, basquetbol: 0 };
    for (const r of rooms) by[r.sport] += r.currentPlayers;
    return by;
  }, [rooms]);

  // El servidor es compartido con la web: la app solo de béisbol no lista salas de otros deportes.
  const filteredRooms = rooms.filter((r) => isSportEnabled(r.sport) && (sport === 'all' || r.sport === sport));

  return (
    <div className="min-h-screen bg-ink-900">
      <Navbar player={player} onSignOut={signOut} />

      <main className="mx-auto max-w-7xl space-y-8 px-6 py-8">
        {notice && (
          <div
            role="status"
            data-testid="hub-notice"
            className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
          >
            <span>{notice}</span>
            <button onClick={() => setNotice(null)} className="btn-ghost h-8 px-3 text-xs" aria-label="Cerrar aviso">
              ✕
            </button>
          </div>
        )}
        {/* Hero */}
        <HeroCarousel
          featured={featured}
          onSelect={handlePlay}
          onCreatePrivate={handleCreatePrivate}
          onViewStadium={handleViewStadium}
        />

        {/* Filtro de deportes (con un solo deporte no hace falta) */}
        {!SINGLE_SPORT && <CategoryRail active={sport} onChange={setSport} />}

        {/* Carruseles por categoría */}
        <Carousel title="Juegos" subtitle="Elige deporte y modo">
          {featured.map((entry) => (
            <GameCard key={entry.id} entry={entry} onPlay={handlePlay} playing={playingBySport[entry.sport]} />
          ))}
        </Carousel>

        {/* Salas activas */}
        <section className="space-y-3">
          <header className="flex items-end justify-between">
            <div>
              <h2 className="text-display text-xl font-bold text-white">Salas activas</h2>
              <p className="text-sm text-slate-400">
                {connected ? '🟢 Conectado' : '🟡 Conectando…'} · {filteredRooms.length} en vivo
              </p>
            </div>
          </header>

          {filteredRooms.length === 0 ? (
            <div className="card-surface grid place-items-center p-12 text-center text-slate-400">
              No hay salas abiertas en esta categoría. ¡Sé el primero en crear una!
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {filteredRooms.map((room: GameRoomSummary) => (
                <RoomCard key={room.id} room={room} onJoin={handleJoin} />
              ))}
            </div>
          )}
        </section>
      </main>
      <footer className="mx-auto flex max-w-7xl flex-wrap gap-x-5 gap-y-1 px-6 pb-8 text-xs text-slate-500">
        <Link to="/como-se-juega" className="hover:text-slate-300">
          Cómo se juega
        </Link>
        <Link to="/privacidad" className="hover:text-slate-300">
          Privacidad
        </Link>
        <Link to="/terminos" className="hover:text-slate-300">
          Términos
        </Link>
        <span>Versión {__APP_BUILD__}</span>
      </footer>
    </div>
  );
};

// ─────────────────────────────────────────────
// Stub de catálogo — reemplazado por Supabase en Fase 2.
// ─────────────────────────────────────────────
const makeStubCatalog = (): GameCatalogEntry[] => [
  {
    id: 'g_futbol_stadium',
    sport: 'futbol',
    title: 'Estadio Central',
    description: '11 vs 11 con física realista, faltas, tarjetas y lesiones.',
    thumbnailUrl: null,
    heroUrl: null,
    stadiumId: 'stadium_default_futbol',
    tags: ['11v11', 'Físicas realistas', 'PvP'],
  },
  {
    id: 'g_futbol_street',
    sport: 'futbol',
    title: 'Street Kings',
    description: 'Fútbol callejero 5v5 en canchas urbanas con nocturnas.',
    thumbnailUrl: null,
    heroUrl: null,
    stadiumId: 'stadium_default_futbol',
    tags: ['5v5', 'Noche', 'Urbano'],
  },
  {
    id: 'g_beisbol_pro',
    sport: 'beisbol',
    title: 'Béisbol Pro League',
    description: '9 entradas con bateo realista, bullpen y alineaciones. Contra la CPU o en línea.',
    thumbnailUrl: null,
    heroUrl: null,
    stadiumId: 'stadium_default_beisbol',
    tags: ['9 entradas', 'PvP', 'Contra la CPU'],
  },
  {
    id: 'g_basquet_hyper',
    sport: 'basquetbol',
    title: 'Hyper Court',
    description: '5v5 con físicas realistas, mates y arenas dinámicas.',
    thumbnailUrl: null,
    heroUrl: null,
    stadiumId: 'stadium_default_basquetbol',
    tags: ['5v5', 'Físicas', 'PvP'],
  },
  {
    id: 'g_basquet_park',
    sport: 'basquetbol',
    title: 'Park & Dunk',
    description: 'Streetball 3v3 al atardecer en parques urbanos.',
    thumbnailUrl: null,
    heroUrl: null,
    stadiumId: 'stadium_default_basquetbol',
    tags: ['3v3', 'Street', 'Atardecer'],
  },
  {
    id: 'g_futbol_kart',
    sport: 'futbol',
    title: 'Fútbol Kart',
    description: 'Modo vehicular con karts y balón gigante.',
    thumbnailUrl: null,
    heroUrl: null,
    stadiumId: 'stadium_default_futbol',
    tags: ['Karts', 'Caótico', 'Coop'],
  },
];
